import { useState, useEffect } from "react";
import { useTheme } from "@/context/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneTilt, CheckCircle, ShieldCheck, Info, Phone, Clock, ArrowUpRight } from "@phosphor-icons/react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import "@/styles/diotech-site.css";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEVICE_OPTIONS = [
  { v: "server", l: "Server" },
  { v: "notebook", l: "Notebook" },
  { v: "desktop", l: "PC Desktop" },
  { v: "stampante", l: "Stampante" },
  { v: "rete", l: "Rete / Router / Wi-Fi" },
  { v: "nas", l: "NAS / Storage" },
  { v: "software", l: "Software / Applicativo" },
  { v: "altro", l: "Altro" },
];

const Field = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="dt-label">{label} {required && <span className="dt-req">*</span>}</label>
    {children}
  </div>
);

const TermsNotice = () => (
  <div className="rounded-md border border-[#11121322] bg-[#11121310] p-5 flex gap-3" data-testid="public-terms-notice">
    <Info size={20} weight="duotone" className="shrink-0 mt-0.5" />
    <div className="text-sm leading-relaxed space-y-2">
      <p><span className="font-semibold">Se hai un contratto di assistenza attivo con Diotech</span>, il ticket verrà gestito e seguito secondo le condizioni previste dal tuo contratto.</p>
      <p><span className="font-semibold">In assenza di contratto</span>, l'intervento è considerato una consulenza a pagamento: prima di procedere ti ricontatteremo per informarti del <span className="font-semibold">costo stimato dell'intervento</span> e ricevere la tua conferma.</p>
    </div>
  </div>
);

const Hero = () => (
  <section className="dt-hero-bg relative overflow-hidden">
    <div className="dt-grain" aria-hidden="true" />
    <div className="relative z-10 dt-container py-16 lg:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
      <div className="dt-rise">
        <div className="dt-eyebrow mb-6" data-testid="diotech-letterhead"><span className="dt-square" /> Sei già cliente Diotech?</div>
        <h1 className="dt-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.06em]">
          Richiesta assistenza.<br /><span className="text-[#ff852a]">Il supporto parte<br />dal canale giusto.</span>
        </h1>
        <p className="mt-6 max-w-lg text-[15px] leading-[1.75] text-[#b9bcbd]">Compila il modulo con i dettagli del problema: un nostro tecnico ti ricontatterà a breve per organizzare l'intervento, da remoto o in sede.</p>
      </div>
      <div className="dt-rise dt-d2 space-y-3 text-sm text-[#b9bcbd]">
        <div className="flex items-center gap-3 border-b border-white/10 pb-3"><Clock size={18} className="text-[#ff852a]" /> Presa in carico rapida della richiesta</div>
        <div className="flex items-center gap-3 border-b border-white/10 pb-3"><Phone size={18} className="text-[#ff852a]" /> Urgenze: <a href="tel:+393209613945" className="text-[#f8f8f4] hover:text-[#ff852a]">320 961 3945</a></div>
        <div className="flex items-center gap-3"><ShieldCheck size={18} className="text-[#ff852a]" /> Dati trattati solo per finalità di assistenza</div>
      </div>
    </div>
  </section>
);

export default function PublicRequestPage() {
  useDocumentTitle("Richiesta assistenza — Diotech");
  const { pushOverride, popOverride } = useTheme();

  useEffect(() => {
    pushOverride("light");
    return () => popOverride();
  }, [pushOverride, popOverride]);

  const [form, setForm] = useState({ name: "", surname: "", company_name: "", phone: "", email: "", device_type: "", description: "", honeypot: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.phone.trim() || !form.email.trim() || !form.device_type || !form.description.trim()) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/public/tickets`, {
        name: form.name.trim(),
        surname: form.surname.trim(),
        company_name: form.company_name.trim() || null,
        phone: form.phone.trim(),
        email: form.email.trim(),
        device_type: form.device_type,
        description: form.description.trim(),
        honeypot: form.honeypot,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) toast.error("Hai già inviato diverse richieste da questa rete. Riprova più tardi.");
      else if (detail) toast.error(typeof detail === "string" ? detail : "Errore invio. Verifica i campi.");
      else toast.error("Impossibile inviare la richiesta. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-site" data-testid="public-request-page">
      <SiteHeader />
      {!done && <Hero />}

      <section className="dt-orange-band py-16 lg:py-24">
        <div className="relative z-10 dt-container grid lg:grid-cols-[.9fr_1.1fr] gap-10 lg:gap-16 items-start">
          {done ? (
            <div className="lg:col-span-2 dt-paper max-w-2xl mx-auto w-full p-8 sm:p-12 text-center dt-rise" data-testid="public-request-success">
              <CheckCircle size={64} weight="duotone" className="text-[#eb641d] mx-auto mb-4" />
              <span className="dt-kicker">Richiesta ricevuta</span>
              <h1 className="dt-display text-4xl mt-2 mb-4">Grazie!</h1>
              <p className="text-[#5b6152] leading-relaxed">Abbiamo ricevuto la tua richiesta di assistenza. Ti contatteremo al più presto ai recapiti che ci hai fornito per organizzare l'intervento.</p>
              <a href="https://www.diotech.it" className="dt-btn dt-btn-ink mt-8" data-testid="success-back-to-site">Torna a diotech.it <ArrowUpRight size={18} weight="bold" /></a>
            </div>
          ) : (
            <>
              <div className="space-y-6 dt-rise">
                <div className="dt-eyebrow !text-[#111213b0]"><span className="dt-square !bg-[#111213]" /> Prima di inviare</div>
                <h2 className="dt-display text-4xl sm:text-5xl leading-[1.05]">Raccontaci<br />il problema.</h2>
                <p className="text-[15px] leading-relaxed text-[#2a1a0e]">Più dettagli fornisci, prima possiamo aiutarti: dispositivo coinvolto, messaggi di errore e quando si è presentato il problema.</p>
                <TermsNotice />
              </div>

              <form onSubmit={submit} className="dt-paper p-6 sm:p-9 dt-rise dt-d2" data-testid="public-request-form">
                <div style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden="true">
                  <label>Website (lascia vuoto)
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={handleChange("honeypot")} />
                  </label>
                </div>
                <span className="dt-kicker">Ticket di assistenza tecnica</span>
                <h3 className="dt-display text-3xl mt-2 mb-7">Apri un ticket.</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Nome" required><input value={form.name} onChange={handleChange("name")} required autoComplete="given-name" placeholder="Nome" className="dt-field" data-testid="req-name" /></Field>
                  <Field label="Cognome" required><input value={form.surname} onChange={handleChange("surname")} required autoComplete="family-name" placeholder="Cognome" className="dt-field" data-testid="req-surname" /></Field>
                  <Field label="Azienda" className="sm:col-span-2"><input value={form.company_name} onChange={handleChange("company_name")} autoComplete="organization" placeholder="Nome dell'azienda (opzionale)" className="dt-field" data-testid="req-company" /></Field>
                  <Field label="Telefono" required><input type="tel" inputMode="tel" value={form.phone} onChange={handleChange("phone")} required autoComplete="tel" placeholder="Il tuo numero" className="dt-field" data-testid="req-phone" /></Field>
                  <Field label="Email" required><input type="email" value={form.email} onChange={handleChange("email")} required autoComplete="email" placeholder="nome@azienda.it" className="dt-field" data-testid="req-email" /></Field>
                  <Field label="Dispositivo o servizio" required className="sm:col-span-2">
                    <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
                      <SelectTrigger className="dt-field h-auto" data-testid="req-device"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        {DEVICE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`req-device-${o.v}`}>{o.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Descrizione del problema" required className="sm:col-span-2">
                    <textarea rows={5} value={form.description} onChange={handleChange("description")} required placeholder="Descrivi il problema o l'intervento richiesto…" className="dt-field resize-y min-h-[120px]" data-testid="req-description" />
                  </Field>
                </div>

                <button type="submit" disabled={submitting} data-testid="req-submit-btn" className="dt-btn dt-btn-ink w-full justify-between mt-6">
                  {submitting ? "Invio in corso…" : "Invia la richiesta di assistenza"} <PaperPlaneTilt size={18} weight="bold" />
                </button>
                <p className="mt-4 text-[11px] text-[#969e8c] flex items-center gap-1.5"><ShieldCheck size={13} /> I tuoi dati sono trattati solo per finalità di assistenza tecnica. I campi con * sono obbligatori.</p>
              </form>
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
